import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { subMonths, subYears } from 'date-fns';
import {
  type ChartGradient,
  type ChartRangeFilter,
  darkModeColors,
  elementGradientMap,
  formatUTCDate,
  graphNetWorthQuery,
  type MonetaryAmountGraphPointValue,
  monetaryAmountValueInCurrencyMajorUnits,
  SegmentedControl,
  useGraphqlClient,
  useTtDetails,
  MobileChart,
  useLocalThemeContext,
} from '@elements-financial/elements';
import { ClientOnly } from 'remix-utils/client-only';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Skeleton } from '@mui/material';

const ALL: ChartRangeFilter = [subYears(new Date(), 10), new Date()];
const ONE_YEAR: ChartRangeFilter = [subYears(new Date(), 1), new Date()];
const SIX_MONTHS: ChartRangeFilter = [subMonths(new Date(), 6), new Date()];
const THREE_MONTHS: ChartRangeFilter = [subMonths(new Date(), 3), new Date()];

function getFilterStrings(filter: ChartRangeFilter) {
  return [formatUTCDate(filter[0].toISOString()), formatUTCDate(filter[1].toISOString())];
}

const useGraphNetWorthData = (householdID: string, filter: ChartRangeFilter) => {
  const client = useGraphqlClient();
  const { data, isLoading } = useQuery({
    enabled: !!householdID,
    queryKey: ['graphNetWorthQuery', householdID, getFilterStrings(filter).join(' to ')],
    queryFn: () => {
      const [start, end] = filter;
      if (householdID) {
        return graphNetWorthQuery(client, {
          householdID: householdID,
          settings: { numberOfPoints: 12, start, end },
        });
      }
    },
    placeholderData: [],
  });

  return { graphNetWorthData: data, graphNetWorthDataIsLoading: isLoading };
};

export const ChartCard: FC<{ householdID: string; className?: string }> = ({ householdID, className }) => {
  const { t } = useTranslation('home');
  const [filter, setFilter] = useState<ChartRangeFilter>(THREE_MONTHS);
  const { ttDetails, ttDetailsQuery } = useTtDetails({
    householdID: householdID,
    includeNetWorth: true,
  });
  const { graphNetWorthData, graphNetWorthDataIsLoading } = useGraphNetWorthData(householdID, filter);
  const chartGradient: ChartGradient = 'netWorth';
  const { colorPalette } = useLocalThemeContext();

  const netWorthMajorUnits = ttDetails?.netWorth
    ? monetaryAmountValueInCurrencyMajorUnits({
        value: ttDetails.netWorth.value,
        currencyCode: ttDetails.netWorth.currencyCode || 'USD',
      })
    : 0;

  return (
    <div
      className={clsx(
        'min-w-[285px] w-full sm:w-[400px] shadow-mobileTile rounded-[16px] bg-white dark:bg-darkMode-gray-mobileTile h-[400px]',
        'chart-card-mobile',
        className
      )}
    >
      <div className={'flex flex-col justify-between'}>
        <div className={'flex flex-col px-5 pt-5 leading-tight'}>
          <div className={'text-lightMode-gray-light dark:text-darkMode-gray-medium text-sm font-extrabold uppercase'}>
            {t('element-client-mobile-net-worth')}
          </div>
          <div>
            {ttDetailsQuery.isLoading || graphNetWorthDataIsLoading ? (
              <Skeleton
                height={53}
                className="w-1/2"
                variant="text"
                sx={{
                  borderRadius: '5px',
                  backgroundColor: `${darkModeColors.gray.medium}1A`,
                }}
              />
            ) : (
              <div>
                <span className={'text-darkMode-gray-light text-[43px] font-extrabold'}>
                  {netWorthMajorUnits < 0 ? '-$' : '$'}
                </span>
                <span className={'text-white text-[43px] font-extrabold'}>
                  {Math.abs(netWorthMajorUnits).toLocaleString('en', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={'h-[212px] mb-5'}>
          {ttDetailsQuery.isLoading || graphNetWorthDataIsLoading ? (
            <Skeleton
              height={200}
              className="!mx-5"
              variant="rectangular"
              sx={{
                borderRadius: '5px',
                backgroundColor: `${darkModeColors.gray.medium}1A`,
              }}
            />
          ) : (
            <ClientOnly>
              {() =>
                graphNetWorthData && (
                  <MobileChart
                    data={graphNetWorthData.map((value) => {
                      const { value: monetaryValue } = value.value as MonetaryAmountGraphPointValue;
                      return {
                        dateISO: value.dateTime,
                        value: Number(monetaryValue.value),
                        monetaryAmount: {
                          value: Number(monetaryValue.value),
                          currencyCode: monetaryValue.currencyCode,
                          __typename: 'MonetaryAmount',
                        },
                        itemStyle: {
                          color: elementGradientMap({
                            accountDarker: colorPalette?.[600],
                            accountLighter: colorPalette?.[400],
                          }).get(
                            `${
                              Number(monetaryValue.value) < 0 ? `${chartGradient}-negative` : chartGradient
                            }` as ChartGradient
                          ),
                        },
                      };
                    })}
                    rangeFilter={filter}
                  />
                )
              }
            </ClientOnly>
          )}
        </div>
        <div className={'flex flex-row justify-center max-[320px]:w-[250px] w-[280px] self-center'}>
          <SegmentedControl
            options={[
              // {
              //   id: 'all',
              //   label: t('element-client-mobile-graph-filter-past-all'),
              //   rangeFilter: ALL,
              // },
              {
                id: 'one_year',
                label: t('element-client-mobile-graph-filter-past-1-year'),
                rangeFilter: ONE_YEAR,
              },
              {
                id: 'six_months',
                label: t('element-client-mobile-graph-filter-past-6-months'),
                rangeFilter: SIX_MONTHS,
              },
              {
                id: 'three_months',
                label: t('element-client-mobile-graph-filter-past-3-months'),
                rangeFilter: THREE_MONTHS,
              },
            ]}
            onSelect={(option) => {
              setFilter(option.rangeFilter);
            }}
          />
        </div>
      </div>
    </div>
  );
};
